// Content for "Welcome to Okiru" — the internal first-week field guide,
// adapted from the "Welcome to Okiru" onboarding booklet (September 2026)
// into a live-style slide deck for new starters. Nine modules mirror the
// booklet's own section order: Get Connected, Work Together, Find Your
// Tools, Inside Okiru.app, Hours & Progress, Leave & Support, Systems
// Onboarding, Where the Code Lives, and Start Building & Build with Care.
// No policy detail has been added beyond what the source booklet states.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate({
  id: 'welcome-to-okiru',
  title: 'Welcome to Okiru',
  subtitle: 'Okiru Learn',
  modules: [
    { index: 1, name: 'Get Connected' },
    { index: 2, name: 'Work Together' },
    { index: 3, name: 'Find Your Tools' },
    { index: 4, name: 'Inside Okiru.app' },
    { index: 5, name: 'Hours & Progress' },
    { index: 6, name: 'Leave & Support' },
    { index: 7, name: 'Systems Onboarding' },
    { index: 8, name: 'Where the Code Lives' },
    { index: 9, name: 'Start Building & Build with Care' },
  ],
}, 'okiru-light');

export const slides = [
  {
    id: 1, layout: 'title',
    kicker: 'Okiru Learn · Onboarding',
    title: 'Welcome to Okiru',
    lead: 'Your first-week field guide. Find your feet, build with confidence — the people, platforms and practical first steps that take you from day one to your first shipped change.',
    meta: ['9 modules', 'Self-paced', 'Okiru Learn'],
  },
  {
    id: 2, layout: 'statement',
    text: 'By the end of this guide you will have access to every system you need, know your team’s weekly rhythm, understand where Okiru’s code lives, and be ready to make your first small, reviewed change.',
    footnote: 'Come back to any module whenever you need a refresher — this guide is built to be reused, not read once.',
  },
  {
    id: 3, layout: 'agenda',
    kicker: 'The journey', title: 'Nine modules, one first week',
    columns: [
      [
        { time: '01', label: 'Get Connected' },
        { time: '02', label: 'Work Together' },
        { time: '03', label: 'Find Your Tools' },
      ],
      [
        { time: '04', label: 'Inside Okiru.app' },
        { time: '05', label: 'Hours & Progress' },
        { time: '06', label: 'Leave & Support' },
      ],
      [
        { time: '07', label: 'Systems Onboarding' },
        { time: '08', label: 'Where the Code Lives' },
        { time: '09', label: 'Start Building & Build with Care' },
      ],
    ],
  },

  // ---- Module 1: Get Connected ----
  {
    id: 4, layout: 'intro', module: 1,
    kicker: 'Module 01', title: 'Get Connected',
    lead: 'Okiru brings together transformation consulting, AI and digital tools. Start with the people and access you need — the rest comes step by step.',
    outcome: 'You have met your team, collected your kit, and set up your one work identity.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 5, layout: 'numbered', module: 1,
    kicker: 'Module 01', title: 'First day, one step at a time',
    items: [
      { title: 'Meet your people', description: 'Meet your manager and onboarding buddy. Get your huddle invitation and check the weekly rhythm.' },
      { title: 'Complete the essentials', description: 'Submit your starter paperwork and requested profile photo. Ask where current staff policies are kept.' },
      { title: 'Collect and test your kit', description: 'Sign for your laptop, mouse, charger and other equipment. Test Wi-Fi and your work email.' },
      { title: 'Arrange building access', description: 'Arrange your access card and biometrics before your first Thursday in the office. Check any ALX enrolment needs.' },
    ],
  },
  {
    id: 6, layout: 'lead', module: 1,
    kicker: 'Microsoft & Okiru.app', title: 'One work identity. The right access.',
    lead: 'Use the @okiru.co.za account issued by IT for Outlook, Teams and company files. On Okiru.app, use the staff Microsoft sign-in and your own work account.',
    body: 'Accept the invitations assigned to you and ask IT for approved password-manager access and any missing logins. App access, GitHub access and hosting access are all separate — do not assume one login covers everything.',
    callout: { label: 'Need Google tools?', text: 'Ask IT whether an account already exists. If authorised to create one, choose "Use your existing email" and verify your work address — this creates a Google account, not a new Gmail inbox.' },
  },
  {
    id: 7, layout: 'keyterm', module: 1,
    kicker: 'Key reminder', term: 'Keep your account yours',
    definition: 'Use multi-factor authentication whenever you are prompted for it.',
    body: 'Never share passwords, sign-in codes or API keys, and never borrow a colleague’s account. Every login should trace back to exactly one person.',
    callouts: [
      { label: 'Why it matters', text: 'Shared or borrowed logins break the audit trail the moment something needs to be traced back to who did what.' },
    ],
  },

  // ---- Module 2: Work Together ----
  {
    id: 8, layout: 'intro', module: 2,
    kicker: 'Module 02', title: 'Work Together',
    lead: 'Your week has a predictable shape: four remote days, one day in the office, and a short daily huddle that keeps everyone aligned.',
    outcome: 'You know your working hours, when the daily huddle happens, and how the week is structured.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 9, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Your week, your working rhythm',
    columns: 2,
    cards: [
      { title: 'Remote work', description: '08:00–16:00, Monday, Tuesday, Wednesday & Friday.' },
      { title: 'Thursdays in office', description: '09:00–16:00. Access card and biometrics required for building entry.' },
    ],
  },
  {
    id: 10, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'The daily huddle',
    lead: '08:30–09:00, Monday, Tuesday, Wednesday & Friday — thirty minutes, no Thursday huddle.',
    body: 'Cover three things every time: Today (what will I work on?), Blockers (what or who do I need?), and Other matters (what needs a quick decision?).',
  },
  {
    id: 11, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Comfortable. Appropriate. Okiru.',
    lead: 'Wear anything comfortable and office-appropriate. Take every opportunity to wear your Okiru apparel once it arrives.',
    body: 'Keep tasks updated and log time daily. Use To for action and CC for visibility — check who to copy with your manager.',
  },

  // ---- Module 3: Find Your Tools ----
  {
    id: 12, layout: 'intro', module: 3,
    kicker: 'Module 03', title: 'Find Your Tools',
    lead: 'Bookmark these. You only need the access your role uses.',
    outcome: 'You know exactly which platform to open for which job.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 13, layout: 'numbered', module: 3,
    kicker: 'Module 03', title: 'The right place for the right job',
    items: [
      { title: 'Okiru.app — okiru.app', description: 'Our internal workspace: tasks, clients, documents, hours and leave. Use your staff Microsoft sign-in.' },
      { title: 'Okiru Learn — learn.okiru.co.za', description: 'Systems-onboarding tutorials and checklists. Complete them in your own time, outside working hours.' },
      { title: 'Microsoft 365 — your issued work account', description: 'Outlook for email, Teams for meetings, and SharePoint / OneDrive for company files.' },
      { title: 'Okiru toolkit + BizBrain — www.okiru.biz / app.okiru.biz', description: 'The toolkit and BizBrain product are not the internal staff app. Request the right workspace access.' },
      { title: 'Our company website — www.okiru.co.za', description: 'Get to know our services and public brand. Follow our social channels using the website footer links.' },
    ],
  },
  {
    id: 14, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'Can’t see a module?',
    lead: 'Use Settings → Request Access in Okiru.app.',
    body: 'A different site may need a separate invitation — access to Okiru.app does not automatically grant access to Okiru Learn, the toolkit, or BizBrain.',
  },

  // ---- Module 4: Inside Okiru.app ----
  {
    id: 15, layout: 'intro', module: 4,
    kicker: 'Module 04', title: 'Inside Okiru.app',
    lead: 'Start with your task. Keep the context.',
    outcome: 'You know the everyday loop and where to find CRM, Generator and Resources.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 16, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'Open Home, then Projects',
    lead: 'Open Home for an overview, then Projects for the work assigned to you.',
    body: 'Filter to your own tasks and read the brief before starting — context saves rework later.',
  },
  {
    id: 17, layout: 'numbered', module: 4,
    kicker: 'Module 04', title: 'Your everyday loop',
    items: [
      { title: 'Read the brief', description: 'Understand what is being asked before you start.' },
      { title: 'Do the work', description: 'Complete it using the right module for the job.' },
      { title: 'Review the result', description: 'Check it before marking anything done.' },
      { title: 'Update the task & log time', description: 'Keep the record current for your team.' },
    ],
  },
  {
    id: 18, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Where things live',
    columns: 3,
    cards: [
      { title: 'CRM + Clients', description: 'Keep contacts, client context and follow-ups together.' },
      { title: 'Generator', description: 'Prepare drafts and documents; check facts before use.' },
      { title: 'Resources', description: 'Find shared files, templates and HR & People resources.' },
    ],
  },
  {
    id: 19, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'If you get stuck',
    lead: 'Marketing, Finance and other modules appear according to your access.',
    body: 'AI drafts still need your review before anything goes out.',
    callout: { label: 'Blocked?', text: 'Mark the task Blocked, explain what is missing, and raise it early — don’t sit on it.' },
  },

  // ---- Module 5: Hours & Progress ----
  {
    id: 20, layout: 'intro', module: 5,
    kicker: 'Module 05', title: 'Hours & Progress',
    lead: 'Log the work while it’s fresh.',
    outcome: 'You can log time manually or with the timer, and read your own hours.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 21, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Projects → Time Tracking → Log your time',
    items: [
      { title: 'Add today’s time', description: 'Scroll to Log your time and choose + Quick Add.' },
      { title: 'Describe the work', description: 'Enter start/end times, category, project or client, and a short note. Mark Billable only when agreed.' },
      { title: 'Save and check', description: 'Save the entry and check your hours. For earlier days, open your own row in the time table and the relevant date.' },
    ],
  },
  {
    id: 22, layout: 'lead', module: 5,
    kicker: 'Example · A huddle you attended', title: '08:30–09:00 · Meeting · 0.5 h',
    lead: 'Note: Daily huddle. Confirm the billable setting with your manager.',
    body: 'The same pattern applies to any short, recurring meeting — log it as it happens rather than reconstructing it at the end of the week.',
    callout: { label: 'Prefer a timer?', text: 'Choose a category, then Start Timer. Use Stop Timer for breaks or a change of work. Do not also add the same time manually.' },
  },

  // ---- Module 6: Leave & Support ----
  {
    id: 23, layout: 'intro', module: 6,
    kicker: 'Module 06', title: 'Leave & Support',
    lead: 'Ask early. Keep everyone informed.',
    outcome: 'You know how to request leave and where to go when something is not working.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 24, layout: 'lead', module: 6,
    kicker: 'Settings → Leave → Request Leave', title: 'Choose the type, add the detail, submit',
    lead: 'Choose the leave type and dates. Add the reason or supporting document as prompted, then Submit Request.',
    body: 'Follow progress under My Requests, and agree a handover with your team as part of the same conversation.',
    callout: { label: 'Submitted is not yet approved', text: 'Follow the team’s leave policy. The current form asks for a doctor’s note for sick leave; check with HR if you need help.' },
  },
  {
    id: 25, layout: 'comparison', module: 6,
    kicker: 'Module 06', title: 'Need a hand?',
    columns: [
      { heading: 'A module is missing', items: ['Settings → Request Access'] },
      { heading: 'Something is not working', items: ['Settings → Support → Submit an IT concern'] },
    ],
    footnote: 'Locked out of the app? Contact your manager or IT directly. Include the error and what you tried — never a password.',
  },

  // ---- Module 7: Systems Onboarding ----
  {
    id: 26, layout: 'intro', module: 7,
    kicker: 'Module 07', title: 'Systems Onboarding',
    lead: 'Learn the systems. Build your confidence.',
    outcome: 'You know your starter bundle of eight tutorials, and that they are completed in your own time.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 27, layout: 'statement',
    text: 'Complete all tutorials and practical checks outside company working hours — not during company time.',
    footnote: 'Open Okiru Learn from the app or your learner invitation. Ask your manager for the published bundle link and use learner view.',
  },
  {
    id: 28, layout: 'numbered', module: 7,
    kicker: 'Your starter bundle · learn.okiru.co.za', title: 'Okiru Systems Set-Up: Tutorial & Checklist',
    items: [
      { title: 'GitHub + VS Code', description: 'Connect, clone, commit, push.' },
      { title: 'Microsoft', description: 'Outlook, Teams and files.' },
      { title: 'Azure', description: 'Get to know the portal.' },
      { title: 'Claude', description: 'Prompt, artifact and project.' },
      { title: 'Railway', description: 'Connect the approved account.' },
      { title: 'Railway walkthrough', description: 'Follow a deployment end to end.' },
      { title: 'GoDaddy', description: 'Find the domain dashboard.' },
      { title: 'Canva', description: 'Create a first design.' },
    ],
  },
  {
    id: 29, layout: 'lead', module: 7,
    kicker: 'Module 07', title: 'Watch, practise, tick off',
    lead: 'Watch every lesson, practise the steps, and tick off each check as you go.',
    body: 'Only create or connect accounts with company approval — this is the same bundle covered in full in the "Okiru Systems Set-Up: Tutorial & Checklist" presentation on Okiru Learn.',
  },

  // ---- Module 8: Where the Code Lives ----
  {
    id: 30, layout: 'intro', module: 8,
    kicker: 'Module 08', title: 'Where the Code Lives',
    lead: 'Four repositories. Know your workspace.',
    outcome: 'You know which repo holds which product, and that Okiru.biz and Okiru.co.za use pnpm.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 31, layout: 'cards', module: 8,
    kicker: 'Module 08', title: 'A repository is a shared code folder',
    lead: 'Accept your GitHub invitation before trying to clone one.',
    columns: 2,
    cards: [
      { title: 'okiru-agent · main', description: 'The internal Okiru.app workspace. web-app/ = screens; azure-functions/ = API.' },
      { title: 'Okiru.biz · main', description: 'The toolkit and BizBrain product. BizBrain code lives in artifacts/bizbrain/.' },
      { title: 'Okiru-Learn · main', description: 'Courses, learner progress and presentations. Start with server.js and src/.' },
      { title: 'Okiru.co.za · master', description: 'The public website and company profile. Website: artifacts/okiru-website/.' },
    ],
  },
  {
    id: 32, layout: 'lead', module: 8,
    kicker: 'Module 08', title: 'Different repos, different commands',
    lead: 'Read package.json and the lockfile first.',
    body: 'Okiru.biz and Okiru.co.za use pnpm — do not run npm install in every repository, or you risk installing the wrong dependency tree.',
  },

  // ---- Module 9: Start Building & Build with Care ----
  {
    id: 33, layout: 'intro', module: 9,
    kicker: 'Module 09', title: 'Start Building & Build with Care',
    lead: 'Vibe coding starts with a small task, and finishes with someone else reviewing it.',
    outcome: 'You have made — or are ready to make — your first small, reviewed change.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 34, layout: 'lead', module: 9,
    kicker: 'What is vibe coding?', title: 'Describe it, review it, own it',
    lead: 'Vibe coding means describing a change to an AI coding assistant, then reviewing and testing what it produces.',
    body: 'You still own the result — the assistant drafts, you are accountable for what ships.',
  },
  {
    id: 35, layout: 'numbered', module: 9,
    kicker: 'Module 09', title: 'Prepare, open, branch',
    items: [
      { title: 'Prepare the workspace', description: 'Get Git, VS Code, the approved Node.js setup and repo access. Clone the assigned repo to your computer.' },
      { title: 'Open Claude Code', description: 'In VS Code, install Claude Code by Anthropic. Use your approved coding account and choose Plan mode.' },
      { title: 'Work on your own branch', description: 'Create a branch such as onboarding/first-change. Read README and CLAUDE.md / AGENTS.md where present.' },
    ],
  },
  {
    id: 36, layout: 'lead', module: 9,
    kicker: 'Module 09', title: 'Keep the first change low-risk',
    lead: 'Start with a small text or layout fix.',
    body: 'Ask for approved development settings and test data before you begin.',
    callout: { label: 'Never use live data', text: 'Do not use live customer data or production credentials, even for a small change.' },
  },
  {
    id: 37, layout: 'pattern', module: 9,
    kicker: 'A useful first prompt', title: 'Copy this into Claude Code to start',
    lead: 'Use this as your opening message for your first onboarding task, filling in the one detail that’s yours.',
    fields: [
      { label: 'Context', value: 'Read the repo guidance and package.json first. Explain the relevant files. My task is [one small change].' },
      { label: 'Guardrails', value: 'Propose a plan before editing. Use the existing design and avoid unrelated changes. Do not open secret files, expose credentials, change authentication or deploy.' },
      { label: 'After approval', value: 'Make the smallest change, run the relevant checks, and show the diff, results and anything untested.' },
    ],
  },
  {
    id: 38, layout: 'lead', module: 9,
    kicker: 'Module 09', title: 'Review → Test → Request review',
    lead: 'Check the changed files and try the feature locally.',
    body: 'Commit and push your branch, then open a pull request — a request to review your changes. Let the repo owner approve merging and release.',
  },

  // ---- Closing ----
  {
    id: 39, layout: 'outcome',
    kicker: 'Next step', title: 'Leave this guide ready to build',
    checks: [
      'You have access to Okiru.app, Okiru Learn and Microsoft 365, and know which tool is for which job',
      'You know your weekly rhythm: remote hours, Thursdays in office, and the daily huddle',
      'You can log your time and request leave without guessing',
      'You have started — or finished — your systems-onboarding bundle',
      'You know the four repositories and which one holds your work',
      'You have made, or are ready to make, your first small, reviewed change',
    ],
    footnote: 'Keep Okiru.app, Okiru Learn and Meet Okiru bookmarked — you will be back in all three most days.',
  },
];
